import PAYOUT_API from 'api/payout/payout/v1'
import PAYOUT_PROFILES_API from 'api/payout/profiles/v1'
import REFERRALS_API from 'api/referrals/v1'
import axios from 'axios'
import clsx from 'clsx'
import renderAmountField from 'components/FormControls/amountField'
import renderSelect from 'components/FormControls/select'
import { Button, Icon, Spinner, Typography } from 'gu-ui-kit'
import styles from 'modules/Forms/Account/Refund/refund.module.scss'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm, stopSubmit } from 'redux-form'
import validator from 'utils/asyncValidator'
import extractErrors from 'utils/extractErrors'
import formatMoney from 'utils/formatMoney'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import validationSchema from 'modules/Forms/Referral/PayoutRequestForm/validationSchema'
import { notificationShow } from 'modules/Notifications/actions'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { createPath } from 'history'
import qs from 'qs'
import PageHeader from 'components/PageHeader'

//@TODO сделать настройкой приложения
const AVAILABLE_TYPES = ['capitalist', 'cashless']

let PayoutRequestForm = ({
  handleSubmit,
  submitting,
  onClose = () => {},
  form,
  invalid,
  autofill,
  touch,
}) => {
  let axiosCancelToken
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const selector = formValueSelector(form)
  const [helperText, setHelperText] = useState()
  const {
    formValues: { amount, profileId },
  } = useSelector((state) => ({
    formValues: selector(state, 'amount', 'profileId'),
  }))

  const pathGen = useGeneratePathI18n()
  const [payoutProfiles, setPayoutProfiles] = useState([])
  const [currency, setCurrency] = useState('')
  const [loading, setLoading] = useState(true)

  const calculate = useCallback(
    (amount) => {
      if (!profileId) {
        return
      }

      if (axiosCancelToken) {
        axiosCancelToken.cancel()
      }

      axiosCancelToken = axios.CancelToken.source()

      setHelperText(
        <span>
          {t('referral::payout_request.fields.amount.helperText')}
          {'   '}
          <Spinner
            width={12}
            className={styles['refund-amount__spinner']}
          />
        </span>,
      )

      PAYOUT_API.calculate({
        profileId,
        cancelToken: axiosCancelToken.token,
        data: { amount: parseFloat(amount) },
      })
        .then(({ response, error }) => {
          if (error) {
            if (error.canceled) {
              throw new Error('calculation canceled')
            } else {
              throw error
            }
          }
          return response.data
        })
        .then(({ amount, currency }) => {
          setHelperText(
            !!parseFloat(amount) > 0 ? (
              <span>
                {t('referral::payout_request.fields.amount.helperText')}{' '}
                <span
                  className={styles['refund-amount']}
                  dangerouslySetInnerHTML={{
                    __html: `${formatMoney(amount, currency)}`,
                  }}
                />
              </span>
            ) : null,
          )
        })
        .catch((error) => {
          const { form, fields } = extractErrors(error.errors)

          dispatch(stopSubmit('PayoutRequestForm', fields))

          if (form) {
            dispatch(
              notificationShow({
                type: 'error',
                message: form,
                delay: 10000,
              }),
            )
          }
        })
    },
    [setHelperText, axiosCancelToken, profileId, dispatch],
  )

  useEffect(() => {
    calculate(amount)
  }, [profileId])

  useEffect(() => {
    PAYOUT_PROFILES_API.fetchProfiles({})
      .then(({ response: { data } }) => setPayoutProfiles(data))
      .finally(() => setLoading(false))

    REFERRALS_API.fetchInfo().then(({ response: { data } }) => {
      autofill('amount', data.referrer_award_amount.amount)
      touch('amount', data.referrer_award_amount.amount)

      setCurrency(data.referrer_award_amount.currency)
    })
  }, [])

  const submit = useCallback(
    (values, dispatch) =>
      new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: 'REFERRAL/PAYOUT_REQUEST_CREATE',
          payload: { data: { amount: values.amount }, profileId },
          formName: form,
        })
      }).then(onClose),
    [form, profileId],
  )

  return (
    <div className={formStyles['formContainer']}>
      <div className={formStyles['formContainer__inner']}>
        <form onSubmit={handleSubmit(submit)}>
          <PageHeader className={'margin-bottom-32'}>
            {t('referral::payout_request.title')}
          </PageHeader>
          <Typography
            variant={'Body-20'}
            component={'div'}
            className={'margin-bottom-16'}
          >
            {t('referral::payout_request.rules')}
          </Typography>
          <div className={'margin-bottom-16'}>
            <Field
              label={t('referral::payout_request.fields.amount.label')}
              name="amount"
              type="text"
              disabled
              component={renderAmountField}
              t={t}
              fullWidth
              autocomplete="off"
              format={(value) => (value === null ? '' : '' + value)}
              parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
              helperText={helperText}
              simpleHelperStyle
              InputProps={{
                endAdornment: (
                  <Typography
                    variant={'Body-25'}
                    noMargin
                    color={'Label-Secondary'}
                  >
                    {currency}
                  </Typography>
                ),
              }}
            />
          </div>

          <div className={'margin-bottom-16'}>
            <Field
              disabled={payoutProfiles.length === 0}
              label={t('referral::payout_request.fields.profile.label')}
              name="profileId"
              component={renderSelect}
              fullWidth
              t={t}
              showEmptyOption={false}
              noResultsLabel={t('common::empty_results.header')}
              emptyOptionLabel={t('common::select_empty_option')}
              clearOptionLabel={t('common::select_clear_option')}
              ControlProps={{
                startAdornment: loading && <Spinner width={16} />,
                endAdornment: !loading && (
                  <Button
                    color={'Label-GU-Primary'}
                    variant={'link'}
                    size={'s'}
                    component={Link}
                    to={createPath({
                      pathname: pathGen(ROUTES.REFERRAL_PAYOUT_PROFILE_NEW),
                      search: qs.stringify(
                        {
                          goBack: global.location.pathname + global.location.search,
                        },
                        {
                          arrayFormat: 'brackets',
                        },
                      ),
                    })}
                    onClick={(e) => e.stopPropagation()}
                    startIcon={
                      <Icon
                        name={'plus'}
                        size={'s'}
                      />
                    }
                  >
                    {t(
                      `common::buttons.${
                        !loading && payoutProfiles.length === 0 ? 'create' : 'add'
                      }`,
                    )}
                  </Button>
                ),
              }}
              options={payoutProfiles
                .filter(
                  ({ type, status }) => AVAILABLE_TYPES.includes(type) && status === 'approved',
                )
                .map(({ id, type, wallet, currency, name }) => ({
                  value: id,
                  label:
                    t(`referral::payout_profiles_new.fields.type.options.${type}`) +
                    `: ${wallet ? wallet : name} (${currency})`,
                }))}
            />
          </div>

          <div className={clsx(formStyles['formControl__action-buttons'])}>
            <Button
              disabled={submitting}
              onClick={onClose}
              variant={'ghost'}
              grey
              size="l"
              type="button"
            >
              {t('common::buttons.cancel')}
            </Button>
            <Button
              disabled={invalid || submitting}
              size="l"
              variant="primary"
              type="submit"
              bgColor={'Label-GU-Primary'}
            >
              {t(`referral::payout_request.buttons.submit`)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

PayoutRequestForm = reduxForm({
  form: 'PayoutRequestForm',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  forceUnregisterOnUnmount: true,
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
  initialValues: {
    profileId: undefined,
    amount: undefined,
  },
})(PayoutRequestForm)

PayoutRequestForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  form: PropTypes.string,
  invalid: PropTypes.bool,
  change: PropTypes.func,
  autofill: PropTypes.func,
  touch: PropTypes.func,
}

export default PayoutRequestForm
